import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ToolSidebar from "../../utils/ToolSideBar";
import { API_URL, authFetch } from "../../utils/auth";
import "../../../css/superuser/StaffDirectoryPage.css";

export default function JobApplicationDetailPage() {
  const { applicationId } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [note, setNote] = useState("");
  const [stage, setStage] = useState("");
  const [stageNote, setStageNote] = useState("");
  const [interview, setInterview] = useState({ proposed_at: "", scheduling_url: "", internal_note: "" });
  const [emailType, setEmailType] = useState("application_received");
  const [developerDiscipline, setDeveloperDiscipline] = useState("");

  const load = useCallback(async function load() {
    setLoading(true);
    try {
      const response = await authFetch(API_URL + '/ops/job-applications/' + applicationId + '/');
      if (!response.ok) throw new Error('Unable to load application.');
      const data = await response.json();
      setApplication(data);
      setStage(data.status);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  async function post(path, body) {
    setError('');
    setNotice('');
    try {
      const response = await authFetch(API_URL + '/ops/job-applications/' + applicationId + '/' + path + '/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : null;
      if (!response.ok) {
        setError(data?.detail || data?.error || 'Action failed. Server returned ' + response.status + '.');
        return null;
      }
      if (!data) {
        setError('Action completed, but the server did not return JSON. Refresh the page to confirm the latest status.');
        return null;
      }
      setApplication(data.application || data);
      if ((data.application || data)?.status) setStage((data.application || data).status);
      setNotice('Saved.');
      return data;
    } catch (err) {
      setError(err.message || 'Action failed.');
      return null;
    }
  }

  async function saveStage(e) {
    e.preventDefault();
    await post("stage", { status: stage, note: stageNote });
    setStageNote("");
  }

  async function addNote(e) {
    e.preventDefault();
    await post("notes", { body: note });
    setNote("");
  }

  async function scheduleInterview(e) {
    e.preventDefault();
    await post("interviews", interview);
    setInterview({ proposed_at: "", scheduling_url: "", internal_note: "" });
  }

  async function sendEmail(e) {
    e.preventDefault();
    await post("emails", { email_type: emailType });
  }

  async function markHired() {
    await post("hire", { confirm: true, developer_discipline: hireRole === "developer" ? developerDiscipline : "" });
  }

  async function downloadResume() {
    setError("");
    try {
      const downloadUrl = application.resume_download_url || `${API_URL}/ops/job-applications/${applicationId}/?download=resume`;
      const response = await authFetch(downloadUrl);
      if (!response.ok) {
        let detail = "";
        try {
          const data = await response.json();
          detail = data.detail ? ` ${data.detail}` : "";
        } catch (parseError) {
          detail = "";
        }
        setError(`Unable to download resume. Server returned ${response.status}.${detail} Make sure the backend has been deployed/restarted with the resume download endpoint.`);
        return;
      }
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        setError("Resume download endpoint returned JSON instead of a file. The backend needs to be deployed/restarted with the latest code.");
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `${application.name || "applicant"}-resume`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Unable to download resume. ${err.message || "Please try again."}`);
    }
  }

  if (loading) return <div className="staff-shell"><ToolSidebar active="job-applications" /><main className="staff-page"><p>Loading...</p></main></div>;
  if (!application) return <div className="staff-shell"><ToolSidebar active="job-applications" /><main className="staff-page"><p>{error || "Application not found."}</p></main></div>;

  const hireRole = application.portal_role_on_hire || "staff";
  const hiresPartner = hireRole === "partner";
  const hireButtonText = hiresPartner ? "Mark Hired / Connect Partner" : "Mark Hired / Connect Employee";
  const hireHelpText = hiresPartner
    ? "This will create or connect the candidate's partner portal account."
    : `This will assign ${hireRole.replace(/_/g, " ")} portal access.`;

  return (
    <div className="staff-shell">
      <ToolSidebar active="job-applications" />
      <main className="staff-page">
        <header className="staff-header">
          <span>Hiring</span>
          <h1>{application.name}</h1>
          <p>{application.job_title} · {application.stage_label} · Applied {new Date(application.submitted_at).toLocaleString()}</p>
          <Link to="/job-applications">Back to applications</Link>
        </header>

        <div className="staff-detail-grid">
          <section className="staff-card staff-panel-pad">
            <h2>Candidate</h2>
            <dl className="staff-definition-grid">
              <div><dt>Email</dt><dd>{application.email}</dd></div>
              <div><dt>Phone</dt><dd>{application.phone || "Not provided"}</dd></div>
              <div><dt>Location</dt><dd>{application.location || "Not provided"}</dd></div>
              <div><dt>Source</dt><dd>{application.source_label || application.source || "SCD Website"}{application.source_note ? ` · ${application.source_note}` : ""}</dd></div>
              <div><dt>Resume</dt><dd>{application.resume_url ? <button type="button" className="staff-link-button" onClick={downloadResume}>Download resume</button> : "No resume"}</dd></div>
            </dl>
            {application.cover_letter && <><h3>Cover Letter</h3><p className="preserve-lines">{application.cover_letter}</p></>}
          </section>

          <section className="staff-card staff-panel-pad">
            <h2>Status</h2>
            {error && <p className="staff-alert danger">{error}</p>}
            {notice && <p className="staff-alert success">{notice}</p>}
            <form onSubmit={saveStage} className="staff-form-stack">
              <select value={stage} onChange={(e) => setStage(e.target.value)}>
                {application.status_choices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
              </select>
              <textarea value={stageNote} onChange={(e) => setStageNote(e.target.value)} placeholder="Optional internal note for this stage change" />
              <button className="btn btn-primary">Save Stage</button>
            </form>
            <p className="staff-muted">{hireHelpText}</p>
            {hireRole === "developer" && (
              <select value={developerDiscipline} onChange={(e) => setDeveloperDiscipline(e.target.value)}>
                <option value="">Developer Discipline</option>
                <option value="frontend">Frontend</option>
                <option value="backend">Backend</option>
                <option value="fullstack">Full Stack</option>
              </select>
            )}
            <button type="button" className="btn btn-secondary" onClick={markHired}>{hireButtonText}</button>
          </section>
        </div>

        <section className="staff-card staff-panel-pad">
          <h2>Application Answers</h2>
          {application.answers.length === 0 ? <p className="staff-empty inline">No custom answers.</p> : application.answers.map((answer, index) => (
            <div className="answer-row" key={index}><strong>{answer.question}</strong><p className="preserve-lines">{answer.answer || "No answer"}</p></div>
          ))}
        </section>

        <div className="staff-detail-grid">
          <section className="staff-card staff-panel-pad">
            <h2>Add Internal Note</h2>
            <form onSubmit={addNote} className="staff-form-stack">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private hiring note" required />
              <button className="btn btn-primary">Add Note</button>
            </form>
            <div className="note-list">
              {application.notes.length === 0 ? <p>No internal notes yet.</p> : application.notes.map((item) => (
                <article key={item.id}><strong>{item.author}</strong><small>{new Date(item.created_at).toLocaleString()}</small><p className="preserve-lines">{item.body}</p></article>
              ))}
            </div>
          </section>

          <section className="staff-card staff-panel-pad">
            <h2>Interview / Email</h2>
            <form onSubmit={scheduleInterview} className="staff-form-stack">
              <input type="datetime-local" value={interview.proposed_at} onChange={(e) => setInterview({ ...interview, proposed_at: e.target.value })} />
              <input value={interview.scheduling_url} onChange={(e) => setInterview({ ...interview, scheduling_url: e.target.value })} placeholder="Meeting link" />
              <textarea value={interview.internal_note} onChange={(e) => setInterview({ ...interview, internal_note: e.target.value })} placeholder="Internal interview note" />
              <button className="btn btn-secondary">Schedule Interview</button>
            </form>
            <form onSubmit={sendEmail} className="staff-form-stack">
              <select value={emailType} onChange={(e) => setEmailType(e.target.value)}>
                <option value="application_received">Application received</option>
                <option value="screening_request">Screening request</option>
                <option value="interview_invitation">Interview invitation</option>
                <option value="rejection">Rejection</option>
                <option value="offer_next_steps">Offer / next steps</option>
              </select>
              <button className="btn btn-secondary">Send Email</button>
            </form>
          </section>
        </div>

        <section className="staff-card staff-panel-pad">
          <h2>History</h2>
          {application.history.length === 0 ? <p>No history yet.</p> : application.history.map((item) => (
            <div className="history-row" key={item.id}><strong>{item.new_status_label}</strong><small>{item.actor} · {new Date(item.created_at).toLocaleString()}</small>{item.internal_note && <p>{item.internal_note}</p>}</div>
          ))}
        </section>
      </main>
    </div>
  );
}
